import React from 'react';

import { getImagePath } from '@lucid/utils-directus';

import SEO from '../seo/seo';
import type { Lucid } from '../../types/types';

type LucidHeadProps = {
  data: Lucid.Directus.Custom.DirectusData;
  pageContext: Lucid.Context.Keys & Partial<Lucid.Context.Pagination>;
  location?: Partial<Location>;
};

const LucidHead = ({ data, pageContext, location }: LucidHeadProps) => {
  const { directus } = data;
  const node = directus?.node;

  const metatagsItem = node?.translations?.[0].meta_tags;

  const metatagsFromFields = metatagsItem
    ? Object.keys(metatagsItem).reduce((meta, key) => {
        const metatagItem = metatagsItem[key as keyof typeof metatagsItem];

        if (typeof metatagItem === 'string' && key !== 'id') {
          return [
            ...meta,
            {
              key,
              value: metatagItem,
            } as Lucid.Context.Metatag,
          ];
        }

        if (Array.isArray(metatagItem) && key === 'robots') {
          const isMultipleTags = metatagItem.length > 1;

          return [
            ...meta,
            {
              key,
              value: isMultipleTags ? metatagItem.join(',') : metatagItem[0],
            } as Lucid.Context.Metatag,
          ];
        }

        if (typeof metatagItem === 'object' && !Array.isArray(metatagItem) && metatagItem?.filename_disk) {
          return [
            ...meta,
            {
              key: 'og:image',
              value: getImagePath(metatagItem.filename_disk),
            } as Lucid.Context.Metatag,
          ];
        }

        return meta;
      }, [] as Lucid.Context.Metatag[])
    : [];

  const nodeMetatags = [...metatagsFromFields, ...(metatagsItem?.other || [])];

  const { page, metatags: pageMetatags } = pageContext;

  const paginationEntityMetatags =
    page && page > 1
      ? [
          {
            key: 'title',
            value: `Page ${page}${metatagsItem?.title ? ` for ${metatagsItem?.title}` : ''}`,
          },
          {
            key: 'robots',
            value: 'follow, noindex',
          },
        ]
      : undefined;
  const metatags = paginationEntityMetatags ?? (nodeMetatags.length ? nodeMetatags : pageMetatags);

  return <SEO key="seo" location={location} metatags={metatags || []} />;
};

export default LucidHead;
